import { useState } from 'react';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Actividades } from '../piezas/Actividades.jsx';
import { Formulario, type Campo } from '../piezas/Formulario.jsx';
import { Tabla, type Columna } from '../piezas/Tabla.jsx';
import { useMask, useSesion } from '../piezas/Sesion.jsx';
import { txt, TXT_ART } from '../lib/enums.js';
import { aTexto } from '../lib/formato.js';

/**
 * Los diez catalogos, en una pantalla.
 *
 * Diez pantallas de clave y nombre serian diez veces el mismo codigo, asi que
 * el catalogo se DECLARA. Cuatro tienen campos propios y los declaran; los
 * otros seis son clave, nombre y bandera.
 *
 * Dos reglas del estandar que la pantalla impone sola:
 *
 * - Un catalogo se desactiva, nunca se borra. Los registros historicos que lo
 *   citan tienen que seguir siendo legibles, asi que la baja es logica y el
 *   valor desactivado se sigue leyendo aunque ya no se ofrezca.
 * - La clave no se cambia. Es lo que citan las filas viejas y lo que viene en
 *   los CSV del cliente; cambiarla rompe las dos cosas en silencio.
 */

interface Catalogo {
  /** Segmento de la ruta. */
  ruta: string;
  /** Nombre visible, en plural. */
  nombre: string;
  /** Prefijo de la clave, como lo exige el esquema. */
  prefijo: string;
  icono: string;
  /** Columnas propias, ademas de clave, nombre y estatus. */
  propias?: Columna<Fila>[];
  /** Campos propios del formulario. */
  campos?: Campo[];
}

interface Fila extends Record<string, unknown> {
  id: string;
  clave: string;
  nombre: string;
  activo: boolean;
}

const CATALOGOS: Catalogo[] = [
  {
    ruta: 'ciudades',
    nombre: 'Ciudades',
    prefijo: 'ciu',
    icono: '🏙',
    propias: [
      {
        campo: 'ventana_inicio',
        titulo: 'Ventana operativa',
        celda: (f) =>
          f['ventana_inicio'] && f['ventana_fin'] ? (
            <>
              {aTexto(f['ventana_inicio']).slice(0, 5)} a {aTexto(f['ventana_fin']).slice(0, 5)}
            </>
          ) : (
            /* Sin ventana propia manda el parametro general. Se dice, porque
               una celda vacia parece un dato que falta. */
            <span className="muted">manda la general</span>
          ),
      },
      {
        campo: 'latitud',
        titulo: 'Coordenadas',
        secundaria: true,
        celda: (f) =>
          f['latitud'] && f['longitud'] ? (
            <span className="mono" style={{ fontSize: 11.5 }}>
              {Number(f['latitud']).toFixed(4)}, {Number(f['longitud']).toFixed(4)}
            </span>
          ) : (
            <span className="muted">—</span>
          ),
      },
    ],
    campos: [
      { nombre: 'ventana_inicio', etiqueta: 'Ventana desde', tipo: 'hora' },
      { nombre: 'ventana_fin', etiqueta: 'Ventana hasta', tipo: 'hora' },
      { nombre: 'latitud', etiqueta: 'Latitud', tipo: 'numero' },
      { nombre: 'longitud', etiqueta: 'Longitud', tipo: 'numero' },
    ],
  },
  {
    ruta: 'estados',
    nombre: 'Estados',
    prefijo: 'edo',
    icono: '🗺',
    propias: [{ campo: 'abreviatura', titulo: 'Abreviatura' }],
    campos: [{ nombre: 'abreviatura', etiqueta: 'Abreviatura', tipo: 'texto' }],
  },
  {
    ruta: 'causales',
    nombre: 'Causales de cierre',
    prefijo: 'cau',
    icono: '⚖',
    propias: [
      { campo: 'imputable_a', titulo: 'Imputable a' },
      {
        /* Esta bandera es la fuente unica de que desenlace gasta saldo de
           contrato. Lo dice la columna para que quien la edite sepa que esta
           tocando la facturacion. */
        campo: 'consume_saldo',
        titulo: 'Consume saldo',
        celda: (f) =>
          f['consume_saldo'] ? (
            <span className="tag t-hot">si, gasta contrato</span>
          ) : (
            <span className="tag t-ok">no lo gasta</span>
          ),
      },
    ],
    campos: [
      {
        nombre: 'imputable_a',
        etiqueta: 'Imputable a',
        tipo: 'texto',
        ayuda: 'Cliente, IDC, carrier o Enteratek.',
      },
      {
        nombre: 'consume_saldo',
        etiqueta: 'Consume saldo del contrato',
        tipo: 'si-no',
        ancho: true,
        ayuda: 'Decide si un cierre con esta causal gasta un servicio contratado.',
      },
    ],
  },
  {
    ruta: 'paqueterias',
    nombre: 'Paqueterias',
    prefijo: 'paq',
    icono: '📮',
    propias: [
      { campo: 'prefijo', titulo: 'Prefijo de guia' },
      {
        campo: 'url_rastreo',
        titulo: 'Plantilla de rastreo',
        secundaria: true,
        celda: (f) =>
          f['url_rastreo'] ? (
            <span className="mono" style={{ fontSize: 11 }}>
              {aTexto(f['url_rastreo'])}
            </span>
          ) : (
            <span className="muted">sin liga</span>
          ),
      },
    ],
    campos: [
      { nombre: 'prefijo', etiqueta: 'Prefijo de guia', tipo: 'texto' },
      {
        nombre: 'url_rastreo',
        etiqueta: 'Plantilla de rastreo',
        tipo: 'texto',
        ancho: true,
        ayuda: 'Donde diga {guia} se sustituye el numero. Asi la liga se arma sola.',
      },
    ],
  },
  {
    ruta: 'tipos-servicio',
    nombre: 'Tipos de servicio',
    prefijo: 'ser',
    icono: '🛠',
    /* La duracion esperada sale en la LISTA y no solo en el editor.
    
       El editor de actividades esta dentro del dialogo, y el dialogo solo se
       abre para quien escribe catalogos. Planeacion solo lee, y es justo
       quien necesita estas duraciones: son la base de un programa de rollout.
       Sin esta columna la pantalla le ensenaba el catalogo y le escondia el
       dato por el que la tabla existe. */
    propias: [
      {
        campo: 'actividades',
        titulo: 'Actividades',
        ordenable: true,
        celda: (f: Fila) => (
          <span className={Number(f['actividades'] ?? 0) > 0 ? '' : 'muted'}>
            {Number(f['actividades'] ?? 0) > 0 ? String(f['actividades']) : 'sin capturar'}
          </span>
        ),
      },
      {
        campo: 'minutos_esperados',
        titulo: 'Duracion esperada',
        ordenable: true,
        /* En horas y minutos. 185 no significa nada de un golpe de vista y
           ademas es el tipo de numero crudo que el estandar no deja imprimir
           en una celda. */
        celda: (f: Fila) => <span>{enHoras(Number(f['minutos_esperados'] ?? 0))}</span>,
      },
    ],
  },
  { ruta: 'tipos-sitio', nombre: 'Tipos de sitio', prefijo: 'tsi', icono: '🏬' },
  { ruta: 'formatos-sitio', nombre: 'Formatos de sitio', prefijo: 'fmt', icono: '📐' },
  {
    ruta: 'tipos-identificacion',
    nombre: 'Tipos de identificacion',
    prefijo: 'tid',
    icono: '🪪',
  },
  { ruta: 'motivos-ajuste', nombre: 'Motivos de ajuste', prefijo: 'maj', icono: '🧮' },
  {
    /* Articulos y almacenes no son "catalogos simples" pero se administran
       igual, y meterlos aqui evita dos pantallas mas con la misma forma. El
       catalogo de articulos es POR CLIENTE: su alcance lo recorta RLS. */
    ruta: 'articulos',
    nombre: 'Articulos',
    prefijo: 'art',
    icono: '🔧',
    propias: [
      {
        campo: 'numero_parte',
        titulo: 'Numero de parte',
        ordenable: true,
        celda: (f) => <span className="folio">{aTexto(f['numero_parte'])}</span>,
      },
      {
        campo: 'tipo',
        titulo: 'Tipo',
        celda: (f) => (
          <>
            {txt(TXT_ART, f['tipo'] as string)}
            {f['controla_serie'] === true && (
              <span className="tag t-cold" style={{ marginLeft: 6 }}>
                serializado
              </span>
            )}
          </>
        ),
      },
      { campo: 'unidad', titulo: 'Unidad', secundaria: true },
      {
        campo: 'costo_unitario_centavos',
        titulo: 'Costo unitario',
        num: true,
        monto: true,
        secundaria: true,
        celda: (f) => <Dinero centavos={f['costo_unitario_centavos']} moneda={f['moneda']} />,
      },
    ],
    campos: [
      {
        nombre: 'cliente_id',
        etiqueta: 'Cliente',
        tipo: 'catalogo',
        catalogo: 'clientes',
        requerido: true,
        soloAlta: true,
        ayuda: 'Dos clientes pueden usar el mismo numero de parte para cosas distintas.',
      },
      { nombre: 'numero_parte', etiqueta: 'Numero de parte', tipo: 'texto', requerido: true },
      {
        nombre: 'tipo',
        etiqueta: 'Tipo',
        tipo: 'lista',
        requerido: true,
        valores: Object.entries(TXT_ART).map(([valor, etiqueta]) => ({ valor, etiqueta })),
      },
      { nombre: 'codigo_barras', etiqueta: 'Codigo de barras', tipo: 'texto' },
      { nombre: 'unidad', etiqueta: 'Unidad', tipo: 'texto', ayuda: 'PZA, MTS, KG…' },
      {
        nombre: 'controla_serie',
        etiqueta: 'Controla numero de serie',
        tipo: 'si-no',
        ayuda: 'Si cada pieza se rastrea una por una.',
      },
      { nombre: 'costo_unitario_centavos', etiqueta: 'Costo unitario', tipo: 'dinero' },
      { nombre: 'comentarios_manejo', etiqueta: 'Comentarios de manejo', tipo: 'texto-largo' },
    ],
  },
  {
    ruta: 'almacenes',
    nombre: 'Almacenes',
    prefijo: 'alm',
    icono: '🏭',
    propias: [
      {
        campo: 'tipo',
        titulo: 'Tipo',
        celda: (f) => (
          <span className={f['tipo'] === 'ALM_RESGUARDO' ? 'tag t-cold' : 'tag t-ok'}>
            {f['tipo'] === 'ALM_RESGUARDO' ? 'Resguardo de IDC' : 'CEDIS'}
          </span>
        ),
      },
      { campo: 'direccion', titulo: 'Direccion', secundaria: true },
    ],
    campos: [
      {
        nombre: 'tipo',
        etiqueta: 'Tipo',
        tipo: 'lista',
        requerido: true,
        valores: [
          { valor: 'ALM_CEDIS', etiqueta: 'CEDIS' },
          { valor: 'ALM_RESGUARDO', etiqueta: 'Resguardo de IDC' },
        ],
      },
      {
        nombre: 'idc_id',
        etiqueta: 'IDC del resguardo',
        tipo: 'catalogo',
        catalogo: 'idc',
        ayuda: 'Obligatorio cuando el tipo es resguardo, y solo entonces.',
      },
      { nombre: 'ciudad_id', etiqueta: 'Ciudad', tipo: 'catalogo', catalogo: 'ciudades' },
      {
        nombre: 'surte_de_id',
        etiqueta: 'Se surte de',
        tipo: 'catalogo',
        catalogo: 'almacenes',
        ayuda: 'El CEDIS que lo abastece.',
      },
      { nombre: 'direccion', etiqueta: 'Direccion', tipo: 'texto-largo' },
    ],
  },
];

/** Dinero de una celda de catalogo, con el filtro de perfil aplicado. */
function Dinero({ centavos, moneda }: { centavos: unknown; moneda: unknown }): JSX.Element {
  const mask = useMask();
  return <>{mask(typeof centavos === 'number' ? centavos : null, aTexto(moneda) || 'MXN')}</>;
}

/** Minutos a algo que se lee: 185 son "3 h 5 min". */
function enHoras(minutos: number): string {
  if (minutos <= 0) return '—';
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export function Catalogos(): JSX.Element {
  const { puede } = useSesion();
  const [cual, setCual] = useState(0);
  const [panel, setPanel] = useState<{ fila?: Fila } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const escribe = puede('MOD_CATALOGOS', 'escribir');

  /* Si la lista cambiara de orden esto se rompe, asi que el respaldo es la
     primera entrada y no un acceso directo que puede salir indefinido. */
  const c = CATALOGOS[cual] ?? CATALOGOS[0];
  if (!c) return <Pagehead />;

  const base: Columna<Fila>[] = [
    {
      campo: 'clave',
      titulo: 'Clave',
      ordenable: true,
      celda: (f) => <span className="folio">{f.clave}</span>,
    },
    { campo: 'nombre', titulo: 'Nombre', ordenable: true },
  ];

  const estatus: Columna<Fila> = {
    campo: 'activo',
    titulo: 'Estatus',
    ordenable: true,
    celda: (f) => (
      <span className={f.activo ? 'tag t-ok' : 'tag t-mute'}>
        {f.activo ? 'Activo' : 'Desactivado'}
      </span>
    ),
  };

  const campos: Campo[] = [
    {
      nombre: 'clave',
      etiqueta: 'Clave',
      tipo: 'texto',
      requerido: true,
      soloAlta: true,
      patron: `${c.prefijo}_[a-z0-9_]{3,20}`,
      ayuda: `${c.prefijo}_ + tres a veinte letras. No se cambia: la citan los registros viejos.`,
    },
    { nombre: 'nombre', etiqueta: 'Nombre', tipo: 'texto', requerido: true, ancho: true },
    ...(c.campos ?? []),
    {
      nombre: 'activo',
      etiqueta: 'Activo',
      tipo: 'si-no',
      ancho: true,
      ayuda: 'Un catalogo se desactiva, nunca se borra: lo citan registros historicos.',
    },
  ];

  return (
    <>
      <Pagehead />

      {/* El selector va arriba y no en la nav: son diez catalogos y la nav
          quedaria mas larga que la pantalla. */}
      <div className="chips" style={{ marginBottom: 12 }} role="tablist" aria-label="Catalogos">
        {CATALOGOS.map((x, i) => (
          <button
            key={x.ruta}
            type="button"
            role="tab"
            aria-selected={i === cual}
            className={i === cual ? 'chip on' : 'chip'}
            onClick={() => {
              setCual(i);
              setPanel(null);
            }}
            data-prueba={`catalogo-${x.ruta}`}
          >
            <span aria-hidden="true">{x.icono}</span> {x.nombre}
          </button>
        ))}
      </div>

      <Tabla<Fila>
        /* La clave de React cambia con el catalogo para que la tabla se
           reinicie: sin eso, el orden y la pagina de un catalogo se
           arrastrarian al siguiente y se pediria una columna que no existe. */
        key={c.ruta}
        ruta={c.ruta}
        que={c.nombre.toLowerCase()}
        icono={c.icono}
        columnas={[...base, ...(c.propias ?? []), estatus]}
        llave={(f) => f.clave}
        buscable
        csv
        orden="nombre"
        recarga={recarga}
        {...(escribe ? { alElegir: (fila: Fila) => setPanel({ fila }) } : {})}
        acciones={
          escribe ? (
            <button
              type="button"
              className="btn pri"
              onClick={() => setPanel({})}
              data-prueba="alta"
            >
              + Registro
            </button>
          ) : undefined
        }
      />

      {/* Las actividades viven DENTRO del dialogo del tipo de servicio.
      
          Dentro y no debajo: el dialogo es `aria-modal="true"`, asi que todo
          lo que se dibuje atras queda visible y fuera de alcance —el puntero
          no llega y un lector de pantalla tampoco—. Ya paso una vez con el
          panel de tarifas y costo media hora de perseguir un clic que se
          quedaba esperando contra el panel que estaba encima. */}
      {panel && (
        <Formulario
          key={`${c.ruta}-${panel.fila?.clave ?? 'nuevo'}`}
          ruta={c.ruta}
          campos={campos}
          titulo={panel.fila ? `${c.nombre}: ${panel.fila.clave}` : `Alta en ${c.nombre}`}
          {...(c.ruta === 'tipos-servicio' && panel.fila
            ? {
                extra: <Actividades servicio={String(panel.fila.clave)} puedeEscribir={escribe} />,
              }
            : {})}
          actual={panel.fila}
          llave={panel.fila?.clave}
          alCerrar={() => setPanel(null)}
          alGuardar={() => {
            setPanel(null);
            setRecarga(recarga + 1);
          }}
        />
      )}
    </>
  );
}
